import { UsedStamp } from './UsedStamp'
import { CompanyAvatar } from '@/components/CompanyAvatar'
import { Badge } from '@/components/ui'
import { cn } from '@/lib/cn'
import { shortOfferLabel } from '@shared/offers'
import type { Offer } from '@shared/model'

/**
 * En rad i häftet.
 *
 * Blickfånget är rabattens storlek – "25 %", "2 för 1" – och inget annat.
 * Det står redan i designguiden att raden ska fungera så, men raden gjorde
 * länge tvärtom: titeln fick den stora stilen medan rabatten satt som en
 * liten bricka längst till höger, där ögat hittar den sist.
 *
 * Företagsnamnet står inte längre som egen rad. Loggan säger redan vilket
 * företag det är, och en rad mindre är en rad mer luft.
 *
 * Finstilten står inte heller här. Den fanns med en kort period, men en rad
 * villkor per erbjudande lade tjugo pixlar på varje rad – över trettio
 * erbjudanden blev det drygt en extra skärm att scrolla, för text ingen läser
 * medan de letar. Villkoren står i sin helhet på erbjudandets egen sida,
 * vilket är där man läser dem innan man går in i butiken.
 *
 * Loggan är i full färg, till skillnad från tidigare. Hierarkin kommer från
 * storleken i stället för från dämpning: en logga på 48 pixlar konkurrerar
 * inte med en siffra i 28. Det gör att företagen ser ut som sig själva, vilket
 * är vad de betalar för.
 */
export function OfferCard({
  offer,
  brandColor,
  remaining,
  owned,
  onClick,
}: {
  offer: Offer
  /** Företagets profilfärg, när företaget har en. */
  brandColor?: string | null
  remaining: number
  owned: number
  onClick: () => void
}) {
  const usedUp = remaining <= 0

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`${shortOfferLabel(offer.offerTerms)} hos ${offer.companyName}: ${offer.title}${
        usedUp ? ' – redan använd' : ''
      }`}
      className={cn(
        'relative flex w-full items-center gap-4 overflow-hidden rounded-card border p-4 text-left',
        'transition-[scale,border-color] duration-(--duration-tap) ease-(--ease-out)',
        'active:scale-[0.98]',
        // En förbrukad rad är nedsänkt i stället för nedtonad. Nedtonat läser
        // som "laddar"; nedsänkt och stämplat läser som "slut".
        usedUp
          ? 'border-border bg-surface-2'
          : 'border-border bg-surface shadow-card hover:border-border-strong',
      )}
    >
      {/* Profilfärgen som smal remsa. Tre pixlar räcker för att skilja två
          företag åt i en scrollande lista utan att fylla raden. */}
      {brandColor && !usedUp && (
        <span
          aria-hidden
          className="absolute inset-y-0 left-0 w-[3px]"
          style={{ backgroundColor: brandColor }}
        />
      )}

      <CompanyAvatar
        name={offer.companyName}
        logoUrl={offer.companyLogoUrl}
        size={48}
        className={cn(usedUp && 'grayscale-[0.9] opacity-55')}
      />

      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'font-display text-2xl leading-none font-extrabold tracking-tight',
            usedUp ? 'text-muted' : 'text-ink',
          )}
        >
          {shortOfferLabel(offer.offerTerms)}
        </p>

        <p className={cn('mt-1 truncate text-sm', usedUp ? 'text-faint' : 'text-ink')}>
          {offer.title}
        </p>

      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5">
        {usedUp ? (
          <UsedStamp />
        ) : (
          owned > 1 && <Badge tone="brand">{remaining} kvar</Badge>
        )}
      </div>
    </button>
  )
}
